import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { AlertCircle, Loader2, Lock } from 'lucide-react';
import { getPasswordPolicyHints, validatePassword } from '../lib/passwordPolicy';
import { syncUserInfo } from '../services/api';
import '../styles/AuthCallback.css';

function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | recovery | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const urlInfo = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    return {
      type: params.get('type') || hashParams.get('type'),
      error: params.get('error') || hashParams.get('error'),
      errorDescription: params.get('error_description') || hashParams.get('error_description'),
      code: params.get('code'),
      accessToken: hashParams.get('access_token'),
      refreshToken: hashParams.get('refresh_token'),
    };
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!supabase) {
        setStatus('error');
        setErrorMessage('Supabaseが設定されていません（REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY）');
        return;
      }

      const rawError = urlInfo.errorDescription || urlInfo.error;
      if (rawError) {
        setStatus('error');
        setErrorMessage(decodeURIComponent(rawError));
        return;
      }

      const subscription = supabase.auth.onAuthStateChange((event) => {
        if (event === 'PASSWORD_RECOVERY') {
          setStatus('recovery');
        }
      });

      try {
        if (urlInfo.code) {
          const { error } = await supabase.auth.exchangeCodeForSession(window.location.href);
          if (error) throw error;
        } else if (urlInfo.accessToken && urlInfo.refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: urlInfo.accessToken,
            refresh_token: urlInfo.refreshToken,
          });
          if (error) throw error;
        }

        const { data } = await supabase.auth.getSession();
        if (!data?.session) {
          setStatus('error');
          setErrorMessage('リンクが無効、または期限切れです。もう一度「パスワード再設定」を行ってください。');
          return;
        }

        if (urlInfo.type === 'recovery') {
          setStatus('recovery');
          return;
        }

        try {
          await syncUserInfo();
        } catch {
          // ignore
        }

        setStatus('success');
        window.setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
      } catch (e) {
        setStatus('error');
        setErrorMessage(e?.message || '認証処理に失敗しました');
      } finally {
        subscription?.data?.subscription?.unsubscribe?.();
      }
    };

    run();
  }, [navigate, urlInfo]);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!supabase) {
      setErrorMessage('Supabaseが設定されていません');
      return;
    }
    const pwd = validatePassword(password);
    if (!pwd.ok) {
      setErrorMessage(pwd.errors[0]);
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage('パスワードが一致しません');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      try {
        await syncUserInfo();
      } catch {
        // ignore
      }

      setStatus('success');
      window.setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
    } catch (e) {
      setStatus('recovery');
      setErrorMessage(e?.message || 'パスワード更新に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-callback-page">
      {/* Background shapes via CSS pseudo-elements, or explicit divs if preferred */}

      <div className="callback-container">
        <header className="callback-header">
          <div className="auth-callback-app-name">Clinical Clerkship</div>
        </header>

        <main className="callback-card">
          {status === 'loading' && (
            <div className="auth-callback-loading-state" role="status" aria-live="polite">
              <div className="auth-callback-loading-spinner"></div>
              <div className="auth-callback-loading-text">認証処理中です...</div>
            </div>
          )}

          {status === 'success' && (
            <div className="auth-callback-success-state">
              <div className="auth-callback-success-title">完了しました</div>
              <p className="auth-callback-error-message" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                自動的に画面遷移します。<br />遷移しない場合は下のボタンを押してください。
              </p>

              <Link to="/dashboard" className="auth-callback-submit-button" style={{ textAlign: 'center', textDecoration: 'none' }}>
                ダッシュボードへ
              </Link>
            </div>
          )}

          {status === 'recovery' && (
            <div className="auth-callback-success-state">
              <h1 className="auth-callback-success-title" style={{ marginBottom: '0.5rem' }}>パスワード再設定</h1>
              <p className="auth-callback-error-message" style={{ marginBottom: '1.5rem' }}>新しいパスワードを設定してください。</p>

              {errorMessage && (
                <div className="auth-alert error" role="alert">
                  <AlertCircle size={18} />
                  <div>{errorMessage}</div>
                </div>
              )}

              <form onSubmit={handleUpdatePassword} style={{ width: '100%' }}>
                <div className="auth-callback-form-group">
                  <label htmlFor="new-password">新しいパスワード</label>
                  <div className="auth-callback-input-wrapper">
                    <Lock className="auth-callback-input-icon" size={18} />
                    <input
                      id="new-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="例: Abcdef12!@#..."
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="auth-callback-password-hint">
                    {getPasswordPolicyHints().map((hint, i) => (
                      <span key={i} style={{ display: 'block' }}>• {hint}</span>
                    ))}
                  </div>
                </div>

                <div className="auth-callback-form-group">
                  <label htmlFor="confirm-password">新しいパスワード（確認）</label>
                  <div className="auth-callback-input-wrapper">
                    <Lock className="auth-callback-input-icon" size={18} />
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="もう一度入力"
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <button type="submit" disabled={submitting} className="auth-callback-submit-button">
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin" size={18} style={{ marginRight: 8 }} />
                      更新中...
                    </>
                  ) : (
                    'パスワードを更新'
                  )}
                </button>
              </form>
            </div>
          )}

          {status === 'error' && (
            <div className="auth-callback-error-state">
              <div className="auth-callback-error-icon-wrapper">
                <AlertCircle size={32} />
              </div>
              <h2 className="auth-callback-error-title">認証に失敗しました</h2>
              <p className="auth-callback-error-message">
                {errorMessage || '不明なエラーです。リンクが無効か期限切れの可能性があります。'}
              </p>

              <Link to="/login" className="auth-callback-retry-button">
                ログイン画面へ戻る
              </Link>
              <Link to="/forgot-password" className="auth-callback-action-link" style={{ fontSize: '0.9rem' }}>
                再設定をやり直す
              </Link>
            </div>
          )}
        </main>
      </div>
      <footer className="auth-callback-footer">
        <div className="auth-callback-footer-inner">
          &copy; {new Date().getFullYear()} Hirosaki University Clinical Clerkship. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

export default AuthCallback;
